import React, { useEffect, useState } from "react";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { MuiInput } from "../../../components/basicComponent/MuiInput";
import { UtilService } from "../../../services/shared/UtilService";
import dayjs from "dayjs";
import { useGlobalState } from "../../../services/context/AuthContext";
import NoDataFlightCard from "../NoDataFlightCard";

interface Coupon {
  code: string;
  description: string;
  discount_value: string;
  discount_percentage?: string;
  coupon_image: string;
  valid_till: string;
}

interface FlightPromoCardProps {
  basePrice: number;
  onApplyCoupon: (ewtsoffer: number, code: string) => void;
  initialCouponCode?: string;
  initialDiscount?: number;
  bookingFor?: "flight" | "hotel";
}

const FlightPromoCard: React.FC<FlightPromoCardProps> = ({
  basePrice,
  onApplyCoupon,
  initialCouponCode = "",
  initialDiscount = 0,
  bookingFor = "hotel",
}) => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  // store these as UPPERCASE to keep comparisons simple & consistent
  const [selectedCoupon, setSelectedCoupon] = useState<string>("");
  const [appliedCoupon, setAppliedCoupon] = useState<string>(
    initialCouponCode?.toUpperCase?.() || ""
  );
  const [inputCoupon, setInputCoupon] = useState<string>(""); // manual input (UPPERCASE)
  const [ewtsDiscount, setEwtsDiscount] = useState<number>(initialDiscount);
  const [error, setError] = useState<string>("");

  const { userInfo } = useGlobalState();
  const currency = userInfo?.currencyCode || "AED";

  useEffect(() => {
    if (!userInfo?.corporate_id) return;
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCoupons = async () => {
    try {
      const payload = {
        corporateId: userInfo?.corporate_id,
        applicableFor: bookingFor,
        couponGetType: "normal",
      };
      const res = await UtilService.getCoupen(payload);
      if (res?.statusMsg === "SUCCESS") {
        setCoupons(res.data || []);
        // pre-select the radio if initialCouponCode matches a listed coupon
        const initUC = initialCouponCode?.toUpperCase?.() || "";
        if (
          initUC &&
          (res.data || []).some((c: Coupon) => c.code.toUpperCase() === initUC)
        ) {
          setSelectedCoupon(initUC);
        }
      }
    } catch {
      // swallow silently; you can surface a UX message if needed
    }
  };

  // helper: does a given UPPERCASE code exist in list?
  const hasCoupon = (codeUC: string) =>
    coupons.some((c) => c.code.toUpperCase() === codeUC);

  const handleApply = async () => {
    const trimmedUC = (inputCoupon || "").trim().toUpperCase();
    const matchedCoupon = coupons.find(
      (c) => c.code.toUpperCase() === trimmedUC
    );

    if (!matchedCoupon) {
      setError("Enter a valid coupon code.");
      return;
    }

    setError("");

    try {
      const corporate_id = parseInt(
        localStorage.getItem("corporate_id") || "100"
      );
      const payload = {
        booking_type: bookingFor,
        corporate_id,
        code: matchedCoupon.code,
        order_value: basePrice,
      };

      const response = await UtilService.ApplyCoupen(payload);
      const discount = parseFloat(response?.data?.discount_amount || "0");

      if (!!response?.data) {
        const codeUC = matchedCoupon.code.toUpperCase();
        setAppliedCoupon(codeUC);
        setSelectedCoupon(codeUC);
        setEwtsDiscount(discount);
        onApplyCoupon(discount, matchedCoupon.code);
      } else {
        handleRemove();
      }
    } catch (err) {
      console.error("Failed to apply coupon", err);
      setError("Failed to apply coupon. Please try again.");
    }
  };

  const handleRemove = () => {
    setAppliedCoupon("");
    setSelectedCoupon("");
    setInputCoupon("");
    setEwtsDiscount(0);
    setError("");
    onApplyCoupon(0, "");
  };

  return (
    <div className="card my-3">
      <div className="card-header card-head-bg">
        <span>
          <LocalOfferOutlinedIcon /> Offers & Promo Code
        </span>
      </div>

      <div className="card-body">
        <div
          className="d-flex align-items-center  user-manage-model"
          style={{ gap: 12 }}
        >
          <MuiInput
            label="Enter Coupon Code"
            placeholder="Enter Coupon Code"
            value={appliedCoupon || inputCoupon}
            onChange={(val: string) => {
              const nextUC = (val || "").toUpperCase();
              setInputCoupon(nextUC);
              setError("");

              // If no coupon is applied yet, keep radio in sync with the text input:
              // - If input matches a listed coupon → check that radio
              // - If input is cleared or doesn't match → uncheck radio
              if (!appliedCoupon) {
                if (hasCoupon(nextUC)) {
                  setSelectedCoupon(nextUC);
                } else {
                  setSelectedCoupon("");
                }
              }
            }}
            disabled={!!appliedCoupon}
          />

          {!appliedCoupon ? (
            <button className="btn btn-success" onClick={handleApply}>
              Apply
            </button>
          ) : (
            <button className="btn btn-danger" onClick={handleRemove}>
              Remove
            </button>
          )}
        </div>

        {error && <div className="text-danger mb-2">{error}</div>}

        {appliedCoupon && ewtsDiscount > 0 && (
          <div className="text-success fw-semibold mb-3">
            🎉 Congratulations! Instant Discount of {currency}{" "}
            <strong>{ewtsDiscount.toFixed(2)}</strong> has been applied
            successfully.
          </div>
        )}

        <div className="coupons-list scrollable-y-auto">
          {coupons.length > 0 ? (
            coupons.map((coupon) => {
              const codeUC = coupon.code.toUpperCase();
              return (
                <div className="card p-2 mt-3 bg-light" key={coupon.code}>
                  <div className="d-flex align-items-center gap-3">
                    <input
                      type="radio"
                      name="coupon"
                      value={codeUC}
                      disabled={!!appliedCoupon}
                      checked={selectedCoupon === codeUC}
                      onChange={() => {
                        setSelectedCoupon(codeUC);
                        setInputCoupon(codeUC); // reflect into input
                        setError("");
                      }}
                    />
                    <div className="flex-grow-1">
                      <p className="fw-bold mb-1">{coupon.code}</p>
                      <div className="text-muted">{coupon.description}</div>
                      <small className="text-muted">
                        Valid till{" "}
                        {dayjs(coupon.valid_till).format("D MMMM YYYY")}
                      </small>
                    </div>
                    {!!coupon.coupon_image && (
                      <img
                        src={coupon.coupon_image}
                        alt="coupon"
                        className="rounded"
                        style={{ width: 60, height: 60, objectFit: "cover" }}
                      />
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-muted mx-3 mt-4">
              <NoDataFlightCard
                headingText="No coupons available"
                icon={<></>}
              />
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlightPromoCard;
